/**
 * Despia + RevenueCat Native Monetization Bridge
 *
 * Uses the despia-native SDK to handle in-app purchases via RevenueCat
 * when running inside the Despia native wrapper (iOS/Android).
 *
 * FLOW (Native — Despia runtime):
 *   1. Detect Despia runtime via user-agent
 *   2. Call despia('revenuecat://launchPaywall?external_id=...&offering=default')
 *   3. On purchase callback (window.onRevenueCatPurchase), check entitlements
 *   4. Backend receives RevenueCat webhook → confirms purchase → frontend polls for status
 *
 * FLOW (Web — non-Despia):
 *   1. Redirect to RevenueCat Web Purchase Link: https://pay.rev.cat/<token>/<userId>
 *   2. If no web purchase token, fall back to Stripe checkout
 *
 * RESTORE:
 *   despia('getpurchasehistory://', ['restoredData']) → check isActive entitlements
 */

import despia from 'despia-native';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

// ─── Platform detection (per Despia docs) ─────────────────────────────────────

const isDespia = navigator.userAgent.toLowerCase().includes('despia');
const isDespiaIOS = isDespia && (
  navigator.userAgent.toLowerCase().includes('iphone') ||
  navigator.userAgent.toLowerCase().includes('ipad')
);
const isDespiaAndroid = isDespia &&
  navigator.userAgent.toLowerCase().includes('android');

// ─── Configuration ────────────────────────────────────────────────────────────

// RevenueCat entitlement IDs — must match RevenueCat dashboard
// Create a single entitlement (e.g. "premium") and link both iOS and Android products to it
const ENTITLEMENT_IDS = {
  premium: 'premium',
  share: 'share',
};

// RevenueCat Web Purchase Link token (loaded from backend config)
let _webPurchaseToken = null;

async function loadWebPurchaseToken() {
  if (_webPurchaseToken !== null) return _webPurchaseToken;
  try {
    const res = await axios.get(`${API}/subscription/revenuecat-keys`, { withCredentials: true });
    _webPurchaseToken = res.data?.web_purchase_token || '';
  } catch {
    _webPurchaseToken = '';
  }
  return _webPurchaseToken;
}

// ─── User ID management ──────────────────────────────────────────────────────

let _currentUserId = null;

/**
 * Set the current user ID for RevenueCat external_id.
 * Call this on login/auth state change.
 */
export function setDespiaUserId(userId) {
  _currentUserId = userId;
  // Also expose globally for native bridge access
  window.__EILI_USER_ID__ = userId;
}

function getUserId() {
  if (_currentUserId) return _currentUserId;
  if (window.__EILI_USER_ID__) return window.__EILI_USER_ID__;
  return 'anonymous';
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Check if the native purchase bridge is available (Despia runtime detected).
 */
export function isAppleBridgeReady() {
  return isDespia;
}

/**
 * Check if running inside Despia runtime.
 */
export function isDespiaRuntime() {
  return isDespia;
}

/**
 * Purchase a subscription.
 *
 * - In Despia: launches RevenueCat native paywall
 * - On web: redirects to RevenueCat Web Purchase Link (or Stripe fallback)
 *
 * @param {string} planKey - 'premium' or 'share'
 * @returns {object|null} - { success, plan, tier } or null if cancelled
 */
export async function purchaseWithApple(planKey) {
  const userId = getUserId();

  if (isDespia) {
    // ── Native purchase via Despia + RevenueCat paywall ──────────────────
    despia(`revenuecat://launchPaywall?external_id=${encodeURIComponent(userId)}&offering=default`);

    // Wait for the purchase callback or cancellation
    const result = await waitForPurchaseResult(planKey);
    return result;
  } else {
    // ── Web fallback: RevenueCat Web Purchase Link ──────────────────────
    const token = await loadWebPurchaseToken();
    if (token) {
      window.location.href = `https://pay.rev.cat/${token}/${encodeURIComponent(userId)}`;
      return null; // Redirect — purchase completes externally
    }

    // No web purchase token configured — fall back to Stripe
    // Return a signal so the calling code knows to use Stripe instead
    throw new Error('FALLBACK_TO_STRIPE');
  }
}

/**
 * Wait for the native purchase to complete via onRevenueCatPurchase callback.
 * After the callback fires, checks entitlements and syncs with backend.
 */
function waitForPurchaseResult(planKey) {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      window.onRevenueCatPurchase = _defaultPurchaseCallback;
      resolve(null); // Timeout treated as cancellation
    }, 5 * 60 * 1000); // 5 minute timeout (user may authenticate with Face ID, etc.)

    window.onRevenueCatPurchase = async () => {
      clearTimeout(timeout);
      window.onRevenueCatPurchase = _defaultPurchaseCallback;

      try {
        // Re-run entitlement check after purchase
        const activePurchases = await checkEntitlements();
        const targetEntitlement = ENTITLEMENT_IDS[planKey] || planKey;
        const hasPurchase = activePurchases.some(
          (p) => p.entitlementId === targetEntitlement
        );

        if (hasPurchase) {
          // Sync with our backend (webhook may also do this, but this is faster)
          try {
            await axios.get(`${API}/subscription/sync-mobile`, { withCredentials: true });
          } catch {
            // Non-blocking — RevenueCat webhook will also sync
          }

          resolve({
            success: true,
            plan: planKey,
            tier: planKey,
          });
        } else {
          // Callback fired but entitlement not found yet
          // Try syncing via backend as fallback
          try {
            const syncResult = await axios.get(`${API}/subscription/sync-mobile`, {
              withCredentials: true,
            });
            if (syncResult.data?.tier && syncResult.data.tier !== 'free') {
              resolve({
                success: true,
                plan: planKey,
                tier: syncResult.data.tier,
              });
              return;
            }
          } catch {
            // Fall through
          }
          resolve(null);
        }
      } catch {
        resolve(null);
      }
    };
  });
}

/**
 * Check active entitlements from the native store via Despia.
 * Returns array of active purchase objects.
 *
 * Per Despia docs: each item has entitlementId, isActive, productId, etc.
 */
export async function checkEntitlements() {
  if (!isDespia) return [];

  try {
    const data = await despia('getpurchasehistory://', ['restoredData']);
    const purchases = data?.restoredData || [];

    // Filter for active purchases only
    const activePurchases = purchases.filter((p) => p.isActive);
    return activePurchases;
  } catch {
    return [];
  }
}

/**
 * Restore previous purchases (required by App Store guidelines).
 *
 * Calls getpurchasehistory:// and checks active entitlements.
 * Syncs with backend to re-grant access.
 */
export async function restoreApplePurchases() {
  if (!isDespia) {
    return { restored: false, message: 'Restore is not available in this app version.' };
  }

  try {
    const data = await despia('getpurchasehistory://', ['restoredData']);
    const purchases = data?.restoredData || [];
    const activePurchases = purchases.filter((p) => p.isActive);

    if (activePurchases.length === 0) {
      return { restored: false, message: 'No active purchases found.' };
    }

    // Check for premium entitlement
    const hasPremium = activePurchases.some((p) => p.entitlementId === 'premium');
    const hasShare = activePurchases.some((p) => p.entitlementId === 'share');

    // Determine best tier
    let restoredTier = 'free';
    let restoredPlan = 'free';
    if (hasShare) {
      restoredTier = 'share';
      restoredPlan = 'share';
    } else if (hasPremium) {
      restoredTier = 'premium';
      restoredPlan = 'premium';
    }

    if (restoredTier === 'free') {
      return { restored: false, message: 'Your subscription has expired.' };
    }

    // Validate with our backend if receipt data available
    const latestActive = activePurchases[activePurchases.length - 1];
    if (latestActive.receipt) {
      try {
        await axios.post(
          `${API}/subscription/validate-receipt`,
          {
            platform: latestActive.store === 'app_store' ? 'ios' : 'android',
            receipt_data: latestActive.receipt,
            product_id: latestActive.productId,
          },
          { withCredentials: true }
        );
      } catch {
        // Non-blocking
      }
    }

    // Also sync via RevenueCat
    try {
      await axios.get(`${API}/subscription/sync-mobile`, { withCredentials: true });
    } catch {
      // Non-blocking
    }

    return {
      restored: true,
      tier: restoredTier,
      plan: restoredPlan,
    };
  } catch {
    return { restored: false, message: 'Failed to restore purchases. Please try again.' };
  }
}

/**
 * Default purchase callback — fires when no active purchase flow is pending.
 * Used to handle background renewals or purchases initiated outside our UI.
 */
async function _defaultPurchaseCallback() {
  try {
    const active = await checkEntitlements();
    if (active.length > 0) {
      // Sync with backend
      await axios.get(`${API}/subscription/sync-mobile`, { withCredentials: true });
      // Notify frontend to refresh subscription state
      window.dispatchEvent(new Event('subscription-changed'));
    }
  } catch {
    // Silent
  }
}

// Set up the default callback on module load
window.onRevenueCatPurchase = _defaultPurchaseCallback;

/**
 * Initialize Despia purchase listeners on app load.
 * Call once from the app's root component.
 *
 * @param {string} userId - The current user's ID
 */
export function initDespiaPurchases(userId) {
  if (!isDespia) return;

  setDespiaUserId(userId);

  // Run an initial entitlement check on app load
  checkEntitlements().then(async (active) => {
    if (active.length > 0) {
      try {
        await axios.get(`${API}/subscription/sync-mobile`, { withCredentials: true });
      } catch {
        // Silent
      }
    }
  });
}
